import React, { useMemo, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Axis, Chart, LIGHT_THEME, LineSeries, PartialTheme, Position, ScaleType, Settings } from '@elastic/charts'
import { CurveType } from '@elastic/charts/dist/utils/curves'
import { EuiPanel, EuiSpacer, EuiSwitch, EuiTitle, EuiToolTip, EuiFlexGroup, EuiFlexItem, EuiPopover, EuiFilterButton, EuiFilterSelectItem, EuiButton } from '@elastic/eui'

import { ReportCampaign, ReportAdGroup, Campaign, CampaignType } from 'api/interfaces'
import { RootState } from 'app/rootReducer'
import { useWhiteLabel } from 'whiteLabel/WhiteLabelContext'

import { EmptyPromptComponent, LoadingComponent, TitleComponent } from './FormattingComponents'
import { resetChartsForCampaignType, toggleViews, toggleClicks, toggleCpc, toggleCpm, toggleCtr, toggleSpend, setSelectedAds, toggleSelectedAd, selectAllAds, unselectAllAds } from './chartsSlice'

interface CampaignChartsProps {
  campaignData?: ReportCampaign[]
  adPerformanceGroups?: ReportAdGroup[]
  campaign?: Campaign
  isLoading: boolean
}

const CampaignCharts: React.FC<CampaignChartsProps> = ({ campaignData, adPerformanceGroups, campaign, isLoading }) => {
  const dispatch = useDispatch()
  const charts = useSelector((state: RootState) => state.charts)
  const whiteLabel = useWhiteLabel()

  const title = 'Performance Charts'
  const [isAdsPopoverOpen, setIsAdsPopoverOpen] = useState(false)

  const availableAds = useMemo(() => {
    if (!adPerformanceGroups) {
      return []
    }

    return adPerformanceGroups.filter(adGroup => adGroup.hasData)
  }, [adPerformanceGroups])

  useEffect(() => {
    if (campaign?.type) {
      dispatch(resetChartsForCampaignType(campaign.type as CampaignType))
    }
  }, [dispatch, campaign?.type])

  useEffect(() => {
    if (availableAds.length > 0) {
      dispatch(setSelectedAds(availableAds.map(ad => ad.adId)))
    }
  }, [dispatch, availableAds])

  const chartData = useMemo(() => {
    if (charts.selectedAds.length === 0) {
      return []
    } else {
      const dateMap = new Map<string, any>()

      charts.selectedAds.forEach(adId => {
        const adGroup = adPerformanceGroups?.find(group => group.adId === adId)
        if (adGroup && adGroup.hasData) {
          adGroup.performanceData.forEach(adData => {
            const existing = dateMap.get(adData.forDate)
            if (existing) {
              existing.views += adData.views
              existing.clicks += adData.clicks
              existing.spend += adData.spend

              existing.ctr = existing.views > 0 ? (existing.clicks / existing.views) * 100 : 0
              existing.cpc = existing.clicks > 0 ? existing.spend / existing.clicks : 0
              existing.cpm = existing.views > 0 ? (existing.spend / existing.views) * 1000 : 0
            } else {
              dateMap.set(adData.forDate, {
                campaignId: campaign?.id || '',
                campaignName: campaign?.name || '',
                campaignType: campaign?.type || '',
                forDate: adData.forDate,
                views: adData.views,
                clicks: adData.clicks,
                ctr: adData.ctr,
                spend: adData.spend,
                cpc: adData.cpc,
                cpm: adData.cpm
              })
            }
          })
        }
      })

      return Array.from(dateMap.values()).sort((a, b) => a.forDate.localeCompare(b.forDate))
    }
  }, [charts.selectedAds, campaignData, adPerformanceGroups, campaign])

  const onAdsButtonClick = () => {
    setIsAdsPopoverOpen(!isAdsPopoverOpen)
  }

  const closeAdsPopover = () => {
    setIsAdsPopoverOpen(false)
  }

  const handleSelectAllAds = () => {
    const allAdIds = availableAds.map(ad => ad.adId)
    dispatch(selectAllAds(allAdIds))
  }

  const handleUnselectAllAds = () => {
    dispatch(unselectAllAds())
  }

  const handleToggleAd = (adId: string) => {
    dispatch(toggleSelectedAd(adId))
  }

  const theme: PartialTheme = {
    lineSeriesStyle: {
      line: {
        strokeWidth: 3
      },
      point: { radius: 5 }
    }
  }

  const isInternetCampaign = campaign?.type === CampaignType.Internet

  if (isLoading) {
    return <LoadingComponent title={title} />
  }

  if (!campaignData || campaignData.length <= 0) {
    return <EmptyPromptComponent title={title} body='There is no campaign data for the selected period.' />
  }

  return (
    <EuiPanel grow={false}>
      <EuiFlexGroup>
        <EuiFlexItem grow={false}>
          <EuiTitle size='m'>
            <h2>{title}</h2>
          </EuiTitle>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup justifyContent='flexStart' gutterSize='s'>
            {availableAds.length > 0 && (
              <EuiFlexItem grow={false}>
                <EuiPopover
                  ownFocus
                  button={
                    <EuiFilterButton size='m' style={{ boxShadow: 'none' }} color='text' iconType='arrowDown' onClick={onAdsButtonClick} isSelected={isAdsPopoverOpen} numFilters={charts.selectedAds.length} hasActiveFilters={charts.selectedAds.length > 0} numActiveFilters={charts.selectedAds.length}>
                      Filter Ads ({availableAds.length})
                    </EuiFilterButton>
                  }
                  isOpen={isAdsPopoverOpen}
                  closePopover={closeAdsPopover}
                  panelPaddingSize='none'>
                  <div className='euiFilterSelect__items'>
                    <EuiFlexGroup gutterSize='none'>
                      <EuiFlexItem>
                        <EuiButton fullWidth style={{ margin: 3, alignContent: 'center', backgroundColor: 'white' }} onClick={handleUnselectAllAds}>
                          Uncheck All
                        </EuiButton>
                      </EuiFlexItem>
                      <EuiFlexItem>
                        <EuiButton fullWidth style={{ margin: 3, alignContent: 'center', backgroundColor: 'white' }} onClick={handleSelectAllAds}>
                          Check All
                        </EuiButton>
                      </EuiFlexItem>
                    </EuiFlexGroup>
                    {availableAds.map(ad => (
                      <EuiFilterSelectItem checked={charts.selectedAds.includes(ad.adId) ? 'on' : undefined} key={`ads-${ad.adId}`} onClick={() => handleToggleAd(ad.adId)}>
                        {ad.adName || `Ad ${ad.adId}`}
                      </EuiFilterSelectItem>
                    ))}
                  </div>
                </EuiPopover>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size='m' />

      <EuiFlexGroup responsive={false} wrap>
        <EuiFlexItem grow={false} style={{ borderBottom: '2px solid #f49342', paddingBottom: '3px' }}>
          <EuiSwitch label={'Views'} checked={charts.showViews} onChange={() => dispatch(toggleViews())} compressed={true} />
        </EuiFlexItem>
        {isInternetCampaign && (
          <EuiFlexItem grow={false} style={{ borderBottom: '2px solid #2B70F7', paddingBottom: '3px' }}>
            <EuiSwitch label='Clicks' checked={charts.showClicks} onChange={() => dispatch(toggleClicks())} compressed={true} />
          </EuiFlexItem>
        )}
        {!whiteLabel?.hideAllSpend() && (
          <EuiFlexItem grow={false} style={{ borderBottom: '2px solid #9170B8', paddingBottom: '3px' }}>
            <EuiSwitch label='Spend' checked={charts.showSpend} onChange={() => dispatch(toggleSpend())} compressed={true} />
          </EuiFlexItem>
        )}
        {isInternetCampaign && (
          <EuiFlexItem grow={false} style={{ borderBottom: '2px solid #54B399', paddingBottom: '3px' }}>
            <EuiToolTip position='top' content='Click-through rate'>
              <EuiSwitch label='CTR' checked={charts.showCtr} onChange={() => dispatch(toggleCtr())} compressed={true} />
            </EuiToolTip>
          </EuiFlexItem>
        )}
        {!whiteLabel?.hideAllSpend() && isInternetCampaign && (
          <EuiFlexItem grow={false} style={{ borderBottom: '2px solid #AA6556', paddingBottom: '3px' }}>
            <EuiToolTip position='top' content='Cost per click'>
              <EuiSwitch label='CPC' checked={charts.showCpc} onChange={() => dispatch(toggleCpc())} compressed={true} />
            </EuiToolTip>
          </EuiFlexItem>
        )}
        {!whiteLabel?.hideAllSpend() && (
          <EuiFlexItem grow={false} style={{ borderBottom: '2px solid #D6BF57' }}>
            <EuiToolTip position='top' content='Cost per thousand views'>
              <EuiSwitch label='CPM' checked={charts.showCpm} onChange={() => dispatch(toggleCpm())} compressed={true} />
            </EuiToolTip>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>

      <EuiSpacer size='m' />

      <Chart size={{ height: 400 }}>
        <Settings theme={theme} baseTheme={LIGHT_THEME} />
        {charts.showViews && <LineSeries data={chartData.map(a => ({ x: a.forDate, y: a.views }))} id='Views' xAccessor='x' yAccessors={['y']} xScaleType={ScaleType.Ordinal} yScaleType={ScaleType.Linear} groupId='views' curve={CurveType.CURVE_MONOTONE_X} color={['#f49342']} />}
        {charts.showClicks && isInternetCampaign && <LineSeries data={chartData.map(a => ({ x: a.forDate, y: a.clicks }))} id='Clicks' xAccessor='x' yAccessors={['y']} xScaleType={ScaleType.Ordinal} yScaleType={ScaleType.Linear} groupId='clicks' curve={CurveType.CURVE_MONOTONE_X} color={['#2B70F7']} />}
        {!whiteLabel?.hideAllSpend() && charts.showSpend && <LineSeries data={chartData.map(a => ({ x: a.forDate, y: a.spend }))} id='Spend' xAccessor='x' yAccessors={['y']} xScaleType={ScaleType.Ordinal} yScaleType={ScaleType.Linear} groupId='spend' curve={CurveType.CURVE_MONOTONE_X} color={['#9170B8']} />}
        {charts.showCtr && isInternetCampaign && (
          <LineSeries
            data={chartData.map(a => ({
              x: a.forDate,
              y: a.views === 0 ? 0 : (a.clicks / a.views) * 100
            }))}
            id='CTR'
            xAccessor='x'
            yAccessors={['y']}
            xScaleType={ScaleType.Ordinal}
            yScaleType={ScaleType.Linear}
            groupId='ctr'
            curve={CurveType.CURVE_MONOTONE_X}
            color={['#54B399']}
          />
        )}
        {!whiteLabel?.hideAllSpend() && charts.showCpm && (
          <LineSeries
            data={chartData.map(a => ({
              x: a.forDate,
              y: a.views === 0 ? 0 : (a.spend / a.views) * 1000
            }))}
            id='CPM'
            xAccessor='x'
            yAccessors={['y']}
            xScaleType={ScaleType.Ordinal}
            yScaleType={ScaleType.Linear}
            groupId='cpm'
            curve={CurveType.CURVE_MONOTONE_X}
            color={['#D6BF57']}
          />
        )}
        {charts.showCpc && isInternetCampaign && <LineSeries data={chartData.map(a => ({ x: a.forDate, y: a.clicks === 0 ? 0 : a.spend / a.clicks }))} id='CPC' xAccessor='x' yAccessors={['y']} xScaleType={ScaleType.Ordinal} yScaleType={ScaleType.Linear} groupId='cpc' curve={CurveType.CURVE_MONOTONE_X} color={['#AA6556']} />}
        <Axis id='views' hide={!charts.showViews} title='Views' position={charts.positionViews} tickFormat={d => `${Number(d).toLocaleString()}`} groupId='views' />
        {isInternetCampaign && <Axis id='clicks' hide={!charts.showClicks} title='Clicks' position={charts.positionClicks} tickFormat={d => `${Number(d).toLocaleString()}`} groupId='clicks' />}
        {!whiteLabel?.hideAllSpend() && <Axis id='spend' hide={!charts.showSpend} title='Spend' position={charts.positionSpend} tickFormat={d => `${Number(d).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`} groupId='spend' />}
        {isInternetCampaign && <Axis id='ctr' hide={!charts.showCtr} title='CTR' position={charts.positionCtr} tickFormat={d => `${Number(d).toFixed(2)}%`} groupId='ctr' />}
        {!whiteLabel?.hideAllSpend() && <Axis id='cpm' hide={!charts.showCpm} title='CPM' position={charts.positionCpm} tickFormat={d => `${Number(d).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`} groupId='cpm' />}
        {!whiteLabel?.hideAllSpend() && isInternetCampaign && <Axis id='cpc' hide={!charts.showCpc} title='CPC' position={charts.positionCpc} tickFormat={d => `${Number(d).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`} groupId='cpc' />}
        <Axis id='bottom' position={Position.Bottom} />
      </Chart>
    </EuiPanel>
  )
}

export default CampaignCharts
